import {
  AGRUPAMENTO_POR_OMISSAO,
  lerAgrupamentoConfig,
  type ClashAgrupamentoConfig,
  type GrupoAnterior,
  type GrupoCalculado,
} from './clashAgrupamento'
import {
  lerRegrasConfig,
  REGRAS_POR_OMISSAO,
  type ClashRegrasConfig,
  type ClassificacaoDoConflito,
} from './clashClassificacao'
import { isEspecialidade, type MapaDeEspecialidades } from './clashEspecialidades'
import { exclusoesPorOmissao, lerExclusoes, regrasSugeridas, type RegraDeExclusao } from './clashExclusao'
import {
  isPriority,
  setTriage,
  temDecisao,
  type ClashPriority,
  type ClashTriageMap,
} from './clashTriage'

// ---------------------------------------------------------------------------
// O que a classificacao e o agrupamento gravam: as regras usadas
// (limiares e hierarquia), as regras de exclusao (clashExclusao.ts),
// o mapa modelo -> especialidade corrigido pelo
// utilizador, o metodo de agrupamento com os parametros, e os grupos
// (titulo, descricao, identidade, se foram editados a mao). Vai no mesmo
// ficheiro da triagem (localStorage), na sessao e no historico das
// corridas.
//
// E as operacoes sobre a triagem, puras:
//   - aplicarClassificacao: escreve o parecer da regra nos conflitos sem
//     parecer, ou cujo parecer veio de uma regra. Nunca por cima de um
//     parecer manual ou de uma proposta importada.
//   - aplicarAgrupamento: poe os conflitos nos grupos calculados. Os
//     grupos editados a mao e os conflitos tirados a mao ficam.
//   - juntarGrupos, tirarDoGrupo, renomearGrupo: as edicoes a mao.
// ---------------------------------------------------------------------------

export interface ClashGrupoGravado {
  titulo: string
  descricao: string
  /** Hash dos GlobalIds ordenados (ver identidadeDoGrupo). */
  identidade: string
  metodo: string
  prioridade: ClashPriority | null
  /** Editado a mao: voltar a agrupar nao lhe toca. */
  manual?: boolean
}

export interface ClashClassificacaoFile {
  regras: ClashRegrasConfig
  /** Regras de exclusao (clashExclusao.ts): as sugeridas e as do
   *  utilizador, ligadas ou nao. */
  exclusoes: RegraDeExclusao[]
  /** So o que o utilizador corrigiu (o resto deduz-se do nome). */
  especialidades: MapaDeEspecialidades
  agrupamento: ClashAgrupamentoConfig
  grupos: Record<string, ClashGrupoGravado>
  classificadoEm: string | null
  agrupadoEm: string | null
}

export function classificacaoVazia(): ClashClassificacaoFile {
  return {
    regras: { ...REGRAS_POR_OMISSAO, hierarquia: [...REGRAS_POR_OMISSAO.hierarquia] },
    exclusoes: regrasSugeridas(),
    especialidades: {},
    agrupamento: { ...AGRUPAMENTO_POR_OMISSAO, misto: { ...AGRUPAMENTO_POR_OMISSAO.misto } },
    grupos: {},
    classificadoEm: null,
    agrupadoEm: null,
  }
}

/** Ha alguma coisa a gravar (diferente do vazio)? */
export function temClassificacao(c: ClashClassificacaoFile): boolean {
  return (
    Object.keys(c.grupos).length > 0 ||
    Object.keys(c.especialidades).length > 0 ||
    c.classificadoEm !== null ||
    c.agrupadoEm !== null ||
    !exclusoesPorOmissao(c.exclusoes) ||
    JSON.stringify(c.regras) !== JSON.stringify(REGRAS_POR_OMISSAO) ||
    JSON.stringify(c.agrupamento) !== JSON.stringify(AGRUPAMENTO_POR_OMISSAO)
  )
}

/** Le a parte gravada; campo estragado fica com o valor por omissao. */
export function lerClassificacao(v: unknown): ClashClassificacaoFile {
  const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>
  const out = classificacaoVazia()
  out.regras = lerRegrasConfig(o.regras)
  out.exclusoes = lerExclusoes(o.exclusoes)
  out.agrupamento = lerAgrupamentoConfig(o.agrupamento)
  const esp = o.especialidades as Record<string, unknown> | undefined
  if (esp && typeof esp === 'object') {
    for (const [k, e] of Object.entries(esp)) {
      if (e === null || isEspecialidade(e)) out.especialidades[k] = e
    }
  }
  const gs = o.grupos as Record<string, unknown> | undefined
  if (gs && typeof gs === 'object') {
    for (const [id, g0] of Object.entries(gs)) {
      const g = g0 as Record<string, unknown> | null
      if (!g || typeof g !== 'object' || typeof g.titulo !== 'string') continue
      out.grupos[id.slice(0, 80)] = {
        titulo: g.titulo.slice(0, 300),
        descricao: typeof g.descricao === 'string' ? g.descricao.slice(0, 4000) : '',
        identidade: typeof g.identidade === 'string' ? g.identidade.slice(0, 64) : '',
        metodo: typeof g.metodo === 'string' ? g.metodo.slice(0, 20) : '',
        prioridade: isPriority(g.prioridade) ? g.prioridade : null,
        ...(g.manual === true ? { manual: true } : {}),
      }
    }
  }
  if (typeof o.classificadoEm === 'string') out.classificadoEm = o.classificadoEm
  if (typeof o.agrupadoEm === 'string') out.agrupadoEm = o.agrupadoEm
  return out
}

// --- Classificacao -> triagem ---------------------------------------------------

export interface ItemClassificado {
  key: string
  classificacao: ClassificacaoDoConflito
  guids?: [string, string] | null
  label?: string
}

/** Lista "EST (valida), ARQ" em ["EST (valida)", "ARQ"]. */
function lista(texto: string): string[] {
  return texto
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

/** A entrada aceita o parecer da regra? Sem parecer, ou parecer de uma
 *  regra. Um parecer manual, de proposta, ou anterior a classificacao
 *  (sem origem) fica. */
export function aceitaRegra(e: ClashTriageMap[string] | undefined): boolean {
  // Reaberto de uma regra de exclusao, sem mais nada: continua sem
  // parecer de classificacao.
  if (!e || !temDecisao({ ...e, foraDaExclusao: undefined })) return true
  return e.origem === 'regra'
}

export function aplicarClassificacao(
  triagem: ClashTriageMap,
  itens: readonly ItemClassificado[],
  agora: string = new Date().toISOString(),
): { triagem: ClashTriageMap; aplicadas: number; mantidas: number; foraDoAmbito: number } {
  let out = triagem
  let aplicadas = 0
  let mantidas = 0
  let foraDoAmbito = 0
  for (const it of itens) {
    const c = it.classificacao
    if (c.prioridade === null) {
      foraDoAmbito++
      // Um parecer que veio de uma regra e ja nao vale (a regra passou a
      // por o conflito fora do ambito, como os marcadores de coordenacao)
      // sai; um parecer manual fica.
      const e = out[it.key]
      if (e?.origem === 'regra') {
        out = setTriage(
          out,
          it.key,
          {
            origem: undefined,
            regra: c.regra,
            tipoConflito: c.tipo,
            priority: undefined,
            assignedTo: undefined,
            notify: [],
            recommendation: undefined,
            tags: [],
          },
          agora,
        )
      }
      continue
    }
    if (!aceitaRegra(out[it.key])) {
      mantidas++
      continue
    }
    out = setTriage(
      out,
      it.key,
      {
        origem: 'regra',
        regra: c.regra,
        tipoConflito: c.tipo,
        nivel: c.nivel ?? undefined,
        priority: c.prioridade,
        assignedTo: c.responsavel,
        notify: lista(c.comunicar),
        recommendation: c.recomendacao,
        tags: c.nivel ? [`Nível ${c.nivel}`] : [],
        ...(it.guids ? { guids: it.guids } : {}),
        ...(it.label ? { label: it.label } : {}),
      },
      agora,
    )
    aplicadas++
  }
  return { triagem: out, aplicadas, mantidas, foraDoAmbito }
}

// --- Agrupamento -> triagem ------------------------------------------------------

/** Os grupos tal como estao na triagem (para manter ids ao reagrupar). */
export function gruposAnteriores(
  triagem: ClashTriageMap,
  grupos: Record<string, ClashGrupoGravado>,
): GrupoAnterior[] {
  const porId = new Map<string, string[]>()
  for (const [k, e] of Object.entries(triagem)) {
    if (!e.group) continue
    const l = porId.get(e.group)
    if (l) l.push(k)
    else porId.set(e.group, [k])
  }
  return [...porId].map(([id, chaves]) => ({ id, chaves, manual: grupos[id]?.manual === true }))
}

/** Chaves tiradas ou fixadas a mao. */
export function chavesFixas(triagem: ClashTriageMap): Set<string> {
  return new Set(Object.entries(triagem).filter(([, e]) => e.grupoFixo).map(([k]) => k))
}

/**
 * Poe os conflitos presentes nos grupos calculados. Os presentes sem
 * grupo novo perdem o grupo automatico (ou da proposta) que tinham; os
 * fixos e os dos grupos manuais ficam. Os ausentes ficam como estavam
 * (o grupo deles conta o "x de y resolvidos").
 */
export function aplicarAgrupamento(
  triagem: ClashTriageMap,
  calculados: readonly GrupoCalculado[],
  gravados: Record<string, ClashGrupoGravado>,
  presentes: ReadonlySet<string>,
  agora: string = new Date().toISOString(),
): { triagem: ClashTriageMap; grupos: Record<string, ClashGrupoGravado> } {
  const manuais = new Set(Object.entries(gravados).filter(([, g]) => g.manual).map(([id]) => id))
  const destino = new Map<string, GrupoCalculado>()
  for (const g of calculados) for (const k of g.chaves) destino.set(k, g)
  let out = triagem
  for (const k of presentes) {
    const e = out[k]
    if (e?.grupoFixo || (e?.group && manuais.has(e.group))) continue
    const g = destino.get(k)
    if (g) {
      if (e?.group !== g.id || e?.groupTitle !== g.titulo) {
        out = setTriage(out, k, { group: g.id, groupTitle: g.titulo }, agora)
      }
    } else if (e?.group) {
      out = setTriage(out, k, { group: undefined, groupTitle: undefined }, agora)
    }
  }
  // Mapa: os calculados, os manuais e os que ainda tem conflitos.
  const grupos: Record<string, ClashGrupoGravado> = {}
  const referidos = new Set(Object.values(out).map((e) => e.group).filter(Boolean) as string[])
  for (const [id, g] of Object.entries(gravados)) {
    if (g.manual || referidos.has(id)) grupos[id] = g
  }
  for (const g of calculados) {
    grupos[g.id] = {
      titulo: g.titulo,
      descricao: g.descricao,
      identidade: g.identidade,
      metodo: g.metodo,
      prioridade: g.prioridade,
    }
  }
  return { triagem: out, grupos }
}

/** Junta os grupos indicados no primeiro (fica com o id dele, e com o
 *  Guid do topico). O grupo resultante e manual. */
export function juntarGrupos(
  triagem: ClashTriageMap,
  grupos: Record<string, ClashGrupoGravado>,
  ids: readonly string[],
  agora: string = new Date().toISOString(),
): { triagem: ClashTriageMap; grupos: Record<string, ClashGrupoGravado> } {
  const unicos = [...new Set(ids)].filter(Boolean)
  if (unicos.length < 2) return { triagem, grupos }
  const alvo = unicos[0]
  const titulo = grupos[alvo]?.titulo ?? Object.values(triagem).find((e) => e.group === alvo)?.groupTitle ?? alvo
  const juntar = new Set(unicos)
  let out = triagem
  for (const [k, e] of Object.entries(triagem)) {
    if (e.group && juntar.has(e.group)) {
      out = setTriage(out, k, { group: alvo, groupTitle: titulo, grupoFixo: true }, agora)
    }
  }
  const novo = { ...grupos }
  const descricoes = unicos.map((id) => grupos[id]?.descricao).filter(Boolean)
  novo[alvo] = {
    ...(grupos[alvo] ?? { identidade: '', metodo: 'manual', prioridade: null, descricao: '' }),
    titulo,
    descricao: [...new Set(descricoes)].join('\n'),
    prioridade: maisAlta(unicos.map((id) => grupos[id]?.prioridade ?? null)),
    manual: true,
  }
  for (const id of unicos.slice(1)) delete novo[id]
  return { triagem: out, grupos: novo }
}

/** Tira conflitos do grupo deles. Ficam fixos (sem grupo): voltar a
 *  agrupar nao os poe noutro. */
export function tirarDoGrupo(
  triagem: ClashTriageMap,
  keys: readonly string[],
  agora: string = new Date().toISOString(),
): ClashTriageMap {
  let out = triagem
  for (const k of keys) {
    if (!out[k]?.group) continue
    out = setTriage(out, k, { group: undefined, groupTitle: undefined, grupoFixo: true }, agora)
  }
  return out
}

/** Renomeia um grupo; fica manual (o titulo nao volta a ser calculado). */
export function renomearGrupo(
  triagem: ClashTriageMap,
  grupos: Record<string, ClashGrupoGravado>,
  id: string,
  titulo: string,
  agora: string = new Date().toISOString(),
): { triagem: ClashTriageMap; grupos: Record<string, ClashGrupoGravado> } {
  const t = titulo.trim().slice(0, 300)
  if (!t) return { triagem, grupos }
  let out = triagem
  for (const [k, e] of Object.entries(triagem)) {
    if (e.group === id) out = setTriage(out, k, { groupTitle: t, grupoFixo: true }, agora)
  }
  const g = grupos[id] ?? { identidade: '', metodo: 'manual', prioridade: null, descricao: '' }
  return { triagem: out, grupos: { ...grupos, [id]: { ...g, titulo: t, manual: true } } }
}

const ORDEM: ClashPriority[] = ['critica', 'alta', 'media', 'baixa']

function maisAlta(ps: readonly (ClashPriority | null)[]): ClashPriority | null {
  let out: ClashPriority | null = null
  for (const p of ps) if (p && (out === null || ORDEM.indexOf(p) < ORDEM.indexOf(out))) out = p
  return out
}
